import PopoverItems from '@/components/PopoverItems/PopoverItems';
import { Badge, BlockStack, IndexTable } from '@shopify/polaris';
import { CustomColumn, LinkStyled, MaxWidthCell, MinWidthCell } from '../../styled';
import { IOrderTable } from '@/types/components/orderReport';
import PopoverInformation from '@/components/PopoverItems/PopoverInformation';
import RegularText from '@/components/RegularText';
import { config } from '@/config';
import { useMemo } from 'react';
import { useGetCrispDataQuery } from '@/redux/query';
import { ORDER_STATUS_TYPE } from '@/constants/enum';
import { useTranslation } from 'react-i18next';

export const RowData = ({
  dataSource,
  showColumns,
  index,
}: {
  dataSource: IOrderTable;
  index: number;
  showColumns: string[];
}) => {
  const { t } = useTranslation('orderReport');
  const { id, createdAt, lineItem, total, payment, dueDate, method, location, shipTo, status, customer, orderNumber, dueTime } =
    dataSource;
  const { data: crispData } = useGetCrispDataQuery({ shop: config.shop });

  const orderItem = useMemo(() => {
    return lineItem.map((item) => ({
      title: item.name,
      imgSrc: item.image,
      quantity: item.quantity || 0,
    }));
  }, [lineItem]);

  const totalOrderItems = useMemo(() => {
    return orderItem.reduce((total, item) => total + item.quantity, 0);
  }, [orderItem]);

  const handleRenderStatus = (status: string) => {
    switch (status) {
      case ORDER_STATUS_TYPE.FULFILLED: {
        return (
          <Badge tone="success" progress="complete">
            {t('reportTable.statusFilter.fulfilled')}
          </Badge>
        );
      }
      case ORDER_STATUS_TYPE.UNFULFILLED: {
        return (
          <Badge tone="attention" progress="incomplete">
            {t('reportTable.statusFilter.unfulfilled')}
          </Badge>
        );
      }
      case ORDER_STATUS_TYPE.DISPATCHED:
        return (
          <Badge progress="partiallyComplete" tone="warning">
            {t('reportTable.statusFilter.dispatch')}
          </Badge>
        );
      case ORDER_STATUS_TYPE.PICKUP:
        return (
          <Badge progress="partiallyComplete" tone="warning">
            {t('reportTable.statusFilter.pickup')}
          </Badge>
        );
      case ORDER_STATUS_TYPE.DELIVERY:
        return (
          <Badge progress="partiallyComplete" tone="warning">
            {t('reportTable.statusFilter.delivery')}
          </Badge>
        );
      case ORDER_STATUS_TYPE.CLOSED:
        return <Badge progress="complete">{t('reportTable.statusFilter.close')}</Badge>;
      default:
        return <Badge progress="complete">{t('reportTable.statusFilter.unavailable')}</Badge>;
    }
  };

  return (
    <>
      {showColumns.map((column) => {
        switch (column) {
          case 'order':
            return (
              <IndexTable.Cell key="order">
                <LinkStyled
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`https://admin.shopify.com/store/${config.storeName}/orders/${id}`, '_blank');
                  }}
                  target="_blank"
                >
                  {`#${orderNumber}`}
                </LinkStyled>
              </IndexTable.Cell>
            );
          case 'createdAt':
            return <IndexTable.Cell key="createdAt">{createdAt}</IndexTable.Cell>;
          case 'item':
            return (
              <IndexTable.Cell key="item">
                <CustomColumn>
                  <PopoverItems
                    title={`${totalOrderItems} ${totalOrderItems > 1 ? 'items' : 'item'}`}
                    items={orderItem}
                    variant="bodyMd"
                    titleWidth="100%"
                  />
                </CustomColumn>
              </IndexTable.Cell>
            );
          case 'total':
            return <IndexTable.Cell key="total">{`${total.toLocaleString()} ${crispData?.data.currency || ''}`}</IndexTable.Cell>;
          case 'payment':
            return (
              <IndexTable.Cell key="payment">
                <Badge progress="complete">
                  {payment.toString().charAt(0).toUpperCase() + payment.toString().slice(1).toLowerCase()}
                </Badge>
              </IndexTable.Cell>
            );
          case 'dueDate':
            return (
              <IndexTable.Cell key="dueDate">
                <BlockStack>
                  <RegularText>{dueDate}</RegularText>
                  <RegularText>{dueTime}</RegularText>
                </BlockStack>
              </IndexTable.Cell>
            );
          case 'method':
            return <IndexTable.Cell key="method">{method}</IndexTable.Cell>;
          case 'location':
            return <IndexTable.Cell key="location">{location}</IndexTable.Cell>;
          case 'shipTo':
            return (
              <IndexTable.Cell key="shipTo">
                <BlockStack>
                  {(shipTo.firstName || shipTo.lastName) && (
                    <RegularText>
                      {shipTo.firstName || ''} {shipTo.lastName || ''}
                    </RegularText>
                  )}
                  <MaxWidthCell dangerouslySetInnerHTML={{ __html: shipTo.address1 }} />
                  <RegularText>{shipTo.city}</RegularText>
                </BlockStack>
              </IndexTable.Cell>
            );
          case 'status':
            return (
              <IndexTable.Cell key="status">
                <MinWidthCell>{handleRenderStatus(status)}</MinWidthCell>
              </IndexTable.Cell>
            );
          case 'customer':
            return (
              <IndexTable.Cell key="customer">
                <CustomColumn>
                  <PopoverInformation
                    title={customer.name}
                    item={{
                      name: customer.name,
                      address: `${customer.city || ''}${customer.country ? `, ${customer.country}` : ''}`,
                      orderCount: customer.numberOfOrders,
                      mail: customer.email,
                      link: `https://admin.shopify.com/store/${config.shop.split('.')[0]}/customers/${customer.id}`,
                    }}
                    variant="bodyMd"
                  />
                </CustomColumn>
              </IndexTable.Cell>
            );
          default:
            return null;
        }
      })}
    </>
  );
};

export default RowData;
